import axios from "axios";
import { getBillingHeaders, trimId } from "./billing-api";

const API_ROOT = process.env.REACT_APP_BACKEND_ROUTE_URL || "";
const BASE = `${API_ROOT}ipd/billing/doctor-payouts`;

export const ENC_OPTIONS = ["OPD", "IPD", "EMERGENCY", "OT"];

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function assertSuccess(data) {
  if (data?.status === "Failed") {
    throw new Error(data.message || "Request failed");
  }
  return data;
}

async function payoutRequest(method, path, { params, body, responseType } = {}) {
  try {
    const res = await axios({
      method,
      url: `${BASE}${path}`,
      params,
      data: body,
      responseType,
      headers: getBillingHeaders(body != null),
    });
    if (responseType === "blob") return res;
    return assertSuccess(res.data);
  } catch (err) {
    if (err.response?.status === 401) {
      window.location.href = "/login";
    }
    const msg =
      err.response?.data?.message ||
      err.response?.data?.detail ||
      err.message ||
      "Request failed";
    const wrapped = new Error(msg);
    wrapped.response = err.response;
    throw wrapped;
  }
}

function buildPeriodParams({ fromDate, toDate, search, encounterTypes }) {
  const params = {
    from_date: fromDate,
    to_date: toDate,
  };
  if (search?.trim()) params.search = search.trim();
  if (encounterTypes?.length) {
    params.encounter_types = encounterTypes.join(",");
  }
  return params;
}

export function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

export function defaultPayPeriod() {
  const now = new Date();
  const day = now.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const start = new Date(now);
  start.setDate(now.getDate() + mondayOffset);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return {
    fromDate: start.toISOString().slice(0, 10),
    toDate: end.toISOString().slice(0, 10),
  };
}

export function formatPayCycleLabel(fromDate, toDate, label) {
  if (label) return label;
  if (!fromDate || !toDate) return "—";
  const fmt = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };
  return `${fmt(fromDate)} – ${fmt(toDate)}`;
}

// ── API calls ─────────────────────────────────────────────────────────────────

/**
 * There is no hospital-wide "calculate payouts" endpoint, and nothing here should
 * pretend otherwise. A payout is worked out when its charge is captured; one
 * patient's can be redone with `calculateAdmissionPayout`, and everybody's with
 * `recalculatePayoutsOnCurrentRules`, which is what the Recalculate button calls.
 *
 * What used to be here was a POST to `/calculate/` -- a route the server never had.
 * It ran on every load of the payouts screen, answered 404 every time, and swallowed
 * the error, so it did nothing but leave a failed request in the console for anyone
 * diagnosing a payout to find and chase.
 */

export async function getPayoutDashboard(filters) {
  const data = await payoutRequest("GET", "/dashboard/", {
    params: buildPeriodParams(filters),
  });
  return normalizeDashboard(data);
}

export async function getPayoutInsights({ fromDate, toDate }) {
  const data = await payoutRequest("GET", "/insights/", {
    params: { from_date: fromDate, to_date: toDate },
  });
  const list = data.insights ?? data.data?.insights ?? [];
  return Array.isArray(list) ? list : [];
}

export async function getActivePayoutRules() {
  const data = await payoutRequest("GET", "/active-rules/");
  const list = data.active_rules ?? data.rules ?? [];
  return Array.isArray(list) ? list.map(mapActiveRule) : [];
}

export async function getProviderPayoutDetail(providerId, { fromDate, toDate }) {
  const data = await payoutRequest("GET", `/providers/${trimId(providerId)}/`, {
    params: { from_date: fromDate, to_date: toDate },
  });
  return mapProviderDetail(data);
}

export async function exportPayoutRegister(filters) {
  const res = await payoutRequest("GET", "/export/", {
    params: buildPeriodParams(filters),
    responseType: "blob",
  });
  const blob = new Blob([res.data], { type: res.headers["content-type"] || "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `doctor-payouts-${filters.fromDate}-${filters.toDate}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function getPayoutRunPreview({ periodFrom, periodTo, providerIds }) {
  const params = { period_from: periodFrom, period_to: periodTo };
  if (providerIds?.length) params.provider_ids = providerIds.join(",");
  const data = await payoutRequest("GET", "/runs/preview/", { params });
  return normalizeRunPreview(data);
}

export async function getPayoutApprovers() {
  const data = await payoutRequest("GET", "/approvers/");
  const list = data.approvers ?? data.items ?? unwrapList(data);
  return Array.isArray(list)
    ? list.map((a) => ({
        code: a.code || a.id || a.approver || "",
        label: a.label || a.name || a.display_name || a.code || "Approver",
      }))
    : [];
}

export async function createPayoutRunWizard(body) {
  const data = await payoutRequest("POST", "/runs/wizard/", { body });
  return unwrapObject(data);
}

export async function downloadPayoutBankFile(runId) {
  const res = await payoutRequest("GET", `/runs/${trimId(runId)}/bank-file/`, {
    responseType: "blob",
  });
  triggerBlobDownload(res, `payout-bank-${trimId(runId)}.csv`);
}

export async function downloadPayoutTdsRegister(runId) {
  const res = await payoutRequest("GET", `/runs/${trimId(runId)}/tds-register/`, {
    responseType: "blob",
  });
  triggerBlobDownload(res, `payout-tds-${trimId(runId)}.csv`);
}

export async function generatePayoutPayslips(runId) {
  const data = await payoutRequest("POST", `/runs/${trimId(runId)}/payslips/generate/`);
  return unwrapObject(data);
}

// ── Payout runs lifecycle (doctor_payout.md § runs) ─────────────────────────────

export async function listPayoutRuns(params = {}) {
  const data = await payoutRequest("GET", "/runs/", { params });
  const list = data.runs ?? data.payout_runs ?? unwrapList(data);
  return Array.isArray(list) ? list.map(mapPayoutRun) : [];
}

export async function getPayoutRunDetail(runId) {
  const data = await payoutRequest("GET", `/runs/${trimId(runId)}/`, {});
  return mapPayoutRunDetail(data);
}

export async function approvePayoutRun(runId, body = {}) {
  const data = await payoutRequest("POST", `/runs/${trimId(runId)}/approve/`, { body });
  return unwrapObject(data);
}

export async function disbursePayoutRun(runId, overrides = {}) {
  const id = trimId(runId);
  const detail = await payoutRequest("GET", `/runs/${id}/`, {});
  const body = buildDisbursePayloadFromRun(detail, { ...overrides, runId: id });
  const data = await payoutRequest("POST", `/runs/${id}/disburse/`, { body });
  return unwrapObject(data);
}

// ── Payout line hold / release (doctor_payout.md § lines) ───────────────────────

export async function holdPayoutLine(lineId, body = {}) {
  const data = await payoutRequest("POST", `/lines/${trimId(lineId)}/hold/`, { body });
  return unwrapObject(data);
}

export async function releasePayoutLine(lineId, body = {}) {
  const data = await payoutRequest("POST", `/lines/${trimId(lineId)}/release/`, { body });
  return unwrapObject(data);
}

// ── Register-only endpoint (lighter than dashboard) ─────────────────────────────

export async function getPayoutRegister(filters) {
  const data = await payoutRequest("GET", "/register/", {
    params: buildPeriodParams(filters),
  });
  const root = unwrapObject(data);
  const list = root.register ?? unwrapList(root);
  return Array.isArray(list) ? list.map(mapRegisterRow) : [];
}

/**
 * Re-price every unpaid doctor payout against the rules in force right now.
 *
 * Covers all patients, not one admission - a doctor's payout comes from every patient
 * admitted under them, so re-pricing a single admission would leave the rest of their money
 * on the rule that has just been changed.
 *
 * Returns as soon as the work is accepted; the re-pricing itself runs on the server and takes
 * a while over a full hospital, so the caller should refresh rather than expect fresh figures
 * in the response.
 */
/**
 * Everything a payout statement prints, for a date range.
 *
 * Without a practitioner it is the hospital's consolidated statement - every doctor with
 * their own lines, then the grand total. With one, it is that doctor's own statement.
 */
export async function fetchPayoutStatement({ fromDate, toDate, providerId } = {}) {
  const params = new URLSearchParams({ from_date: fromDate || "", to_date: toDate || "" });
  if (providerId) params.set("provider_id", providerId);
  const body = await payoutRequest("GET", `/statement/?${params.toString()}`);
  return body?.statement || body || {};
}

/**
 * Pay one payout line something other than the rule worked out.
 *
 * A rule knows what a doctor earned. It does not know the patient has not settled, or that
 * the insurer paid short. Those calls get made anyway; this is where they get recorded, so
 * the payout register describes what was actually paid.
 */
export async function overridePayoutLine(payoutLineId, { amount, reason, by } = {}) {
  return await payoutRequest("POST", `/${payoutLineId}/override/`, {
    body: {
      net_payout_amount: String(amount),
      reason,
      overridden_by: by,
    },
  });
}

/** Put one payout line back on whatever the rule works out. */
export async function clearPayoutLineOverride(payoutLineId, { by } = {}) {
  return await payoutRequest("POST", `/${payoutLineId}/override/`, {
    body: { clear: true, cleared_by: by },
  });
}

export async function recalculatePayoutsOnCurrentRules(body = {}) {
  return await payoutRequest("POST", "/recalculate/", { body });
}

// ── Per-admission calculate / list (doctor_payout.md Step 4 & 5) ────────────────

export async function calculateAdmissionPayout(admissionId, body = {}) {
  const url = `${API_ROOT}ipd_admission/${trimId(admissionId)}/billing/doctor-payouts/calculate/`;
  try {
    const res = await axios({
      method: "POST",
      url,
      data: body,
      headers: getBillingHeaders(true),
    });
    return assertSuccess(res.data);
  } catch (err) {
    if (err.response?.status === 401) window.location.href = "/login";
    throw new Error(err.response?.data?.message || err.message || "Calculate failed");
  }
}

export async function listAdmissionPayoutLines(admissionId) {
  const url = `${API_ROOT}ipd_admission/${trimId(admissionId)}/billing/doctor-payouts/`;
  try {
    const res = await axios({ method: "GET", url, headers: getBillingHeaders() });
    const data = assertSuccess(res.data);
    const root = unwrapObject(data);
    const block = root.payout_lines;
    if (block?.items && Array.isArray(block.items)) return block.items;
    if (Array.isArray(block)) return block;
    if (Array.isArray(root.lines)) return root.lines;
    return unwrapList(root);
  } catch (err) {
    if (err.response?.status === 401) window.location.href = "/login";
    throw new Error(err.response?.data?.message || err.message || "Failed to load payout lines");
  }
}

export async function postAdmissionBillingSetup(admissionId) {
  const url = `${API_ROOT}ipd_admission/${trimId(admissionId)}/billing_setup/`;
  try {
    const res = await axios({ method: "POST", url, headers: getBillingHeaders(true) });
    return assertSuccess(res.data);
  } catch (err) {
    if (err.response?.status === 401) window.location.href = "/login";
    throw new Error(err.response?.data?.message || err.message || "Billing setup failed");
  }
}

export async function postAdmissionCharge(admissionId, body) {
  const url = `${API_ROOT}ipd_admission/${trimId(admissionId)}/billing/charges/`;
  try {
    const res = await axios({
      method: "POST",
      url,
      data: body,
      headers: getBillingHeaders(true),
    });
    return assertSuccess(res.data);
  } catch (err) {
    if (err.response?.status === 401) window.location.href = "/login";
    throw new Error(err.response?.data?.message || err.message || "Failed to post charge");
  }
}

export async function searchChargeMaster({ name, type, limit = 20 }) {
  try {
    const res = await axios.get(`${API_ROOT}search_charge_master/`, {
      headers: getBillingHeaders(),
      params: { name, type, limit },
    });
    const items = res.data?.items ?? unwrapList(res.data);
    return Array.isArray(items) ? items : [];
  } catch (err) {
    if (err.response?.status === 401) window.location.href = "/login";
    throw new Error(err.response?.data?.message || err.message || "Charge search failed");
  }
}

export function parseChargePostResult(data) {
  const root = unwrapObject(data) || data || {};
  return {
    billingLedgerId:
      root.billing_ledger_id ||
      root.ledger_id ||
      root.billing_ledger?.id ||
      "",
    chargeEventId: root.charge_event?.id || root.charge_event_id || "",
    autoPayoutLines:
      root.doctor_payout?.payout_lines ||
      root.payout_lines?.items ||
      (Array.isArray(root.payout_lines) ? root.payout_lines : []),
    message: root.message || data?.message || "",
  };
}

export async function getAdmissionPayoutLinesMapped(admissionId) {
  const list = await listAdmissionPayoutLines(admissionId);
  return list.map((ln, idx) => mapProviderLine(ln, idx));
}

// ── helpers ───────────────────────────────────────────────────────────────────

function unwrapList(data) {
  if (Array.isArray(data)) return data;
  for (const key of ["data", "results", "items", "providers", "register"]) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
}

function unwrapObject(data) {
  if (!data || typeof data !== "object") return data;
  if (data.detail && typeof data.detail === "object" && !Array.isArray(data.detail)) {
    return data.detail;
  }
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  return data;
}

function triggerBlobDownload(res, filename) {
  const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function mapSummaryFields(src = {}) {
  return {
    gross: num(src.gross_billed ?? src.gross),
    payable: num(src.payable_pre_tds ?? src.payable ?? src.payable_rules),
    tds: num(src.tds_amount ?? src.tds),
    net: num(src.net_after_tds ?? src.net ?? src.net_pre_adv),
    advances: num(src.advances ?? src.advances_paid ?? src.advances_offset),
    finalPayout: num(src.final_payout ?? src.final ?? src.net_to_disburse ?? src.final_disbursal),
    recovery: num(src.recovery ?? src.to_recover),
    doctors: num(src.doctors_in_cycle ?? src.doctors ?? src.eligible_doctors),
  };
}

export function mapRegisterRow(row) {
  const byEnc = { OPD: 0, IPD: 0, EMERGENCY: 0, OT: 0 };
  (row.mix || []).forEach((m) => {
    const key = m.encounter_type || m.encounter;
    if (key && byEnc[key] !== undefined) byEnc[key] = num(m.amount);
  });

  const advances = num(row.advances);
  const net = num(row.net);
  const finalPayout = num(row.final);

  return {
    providerId: row.provider_id,
    doc: {
      id: row.provider_id,
      name: row.doctor_name || row.provider_name || "—",
      specialty: row.specialty || row.specialization || "—",
      empType: row.employment_type || row.emp_type || "—",
      panMasked: row.provider_code_masked || row.pan_masked || row.provider_code || "—",
    },
    mix: row.mix || [],
    byEnc,
    linesCount: num(row.lines),
    gross: num(row.gross),
    payable: num(row.payable),
    tds: num(row.tds),
    net,
    advanceTotal: advances,
    finalPayout,
    recovery: num(row.recovery) || Math.max(0, advances - net),
    status: row.status || "ready",
    unmatched: num(row.unmatched_lines ?? row.unmatched),
  };
}

function mapActiveRule(r) {
  const kind = (r.kind || r.rule_kind || r.calculation_type || "percent").toLowerCase();
  const summary = r.summary || "";
  // Active-rules endpoint embeds the rate only in the summary string (e.g. "…: 8.00%")
  const pctFromText = (() => {
    const explicit = r.percent ?? r.percentage;
    if (explicit != null && explicit !== "") return num(explicit);
    const m = summary.match(/([\d.]+)\s*%/);
    return m ? num(m[1]) : null;
  })();
  const flatPerUnit = num(r.flat_per_unit ?? r.flat_amount ?? r.fixed_amount);

  let label;
  if (pctFromText != null && (kind === "percent" || (!flatPerUnit && pctFromText))) {
    label = `${pctFromText}%`;
  } else if (kind === "fixed" || flatPerUnit) {
    label = `₹${flatPerUnit.toLocaleString("en-IN")}`;
  } else {
    label = "Tiered";
  }

  return {
    id: r.rule_definition_id || r.id || r.rule_id || r.rule_code || r.code,
    code: r.rule_code || r.code,
    name: r.rule_name || r.name || "—",
    summary,
    encounter: r.encounter || r.encounter_type || "ANY",
    category: r.category || r.charge_category || "ANY",
    kind,
    percent: pctFromText,
    flatPerUnit,
    tdsPct: r.tds_pct ?? r.tds_percent ?? r.tds,
    label,
  };
}

function normalizeDashboard(data) {
  const root = unwrapObject(data);
  const payCycle = root.pay_cycle || {};
  const cards = mapSummaryFields(root.cards || {});
  const disbursal = mapSummaryFields(root.disbursal_summary || root.cards || {});
  // disbursal_summary has no doctor count / gross may differ — backfill from cards
  disbursal.doctors = disbursal.doctors || cards.doctors;
  disbursal.gross = disbursal.gross || cards.gross;
  const register = (root.register || []).map(mapRegisterRow);

  return {
    payCycle: {
      fromDate: payCycle.from_date || payCycle.fromDate,
      toDate: payCycle.to_date || payCycle.toDate,
      label: payCycle.label,
    },
    cards,
    disbursal,
    register,
    total: num(root.total) || register.length,
  };
}

function normalizeRunPreview(data) {
  const root = unwrapObject(data);
  // EMR shape: { status, wizard_step, preview: { doctors: [...], net_pre_adv, ... } }
  const previewBlock = root.preview && typeof root.preview === "object" && !Array.isArray(root.preview)
    ? root.preview
    : null;
  const rawList =
    (previewBlock && (previewBlock.doctors || previewBlock.providers)) ||
    root.doctors ||
    root.providers ||
    root.eligible ||
    (Array.isArray(root.register) ? root.register : null) ||
    (Array.isArray(root.preview) ? root.preview : null) ||
    unwrapList(root);
  const providers = (Array.isArray(rawList) ? rawList : []).map(mapPreviewProvider);
  const summary = mapSummaryFields(
    root.summary || root.totals || previewBlock || {},
  );

  return { providers, summary };
}

function mapPreviewProvider(row) {
  const mapped = mapRegisterRow(row);
  return {
    ...mapped,
    eligible: row.eligible !== false,
    status: row.status || mapped.status,
  };
}

function extractProviderLines(root) {
  const block = root.payout_lines;
  if (block?.items && Array.isArray(block.items)) return block.items;
  if (Array.isArray(block)) return block;
  if (Array.isArray(root.lines)) return root.lines;
  if (Array.isArray(root.charge_lines)) return root.charge_lines;
  return unwrapList(root);
}

function mapProviderLine(ln, idx) {
  const ruleExplain = typeof ln.rule === "string"
    ? ln.rule
    : (ln.rule_explanation || ln.explain || ln.calculation_note || "");
  const ruleName = ln.rule_name || ln.rule_code
    || (typeof ln.rule === "object" && ln.rule ? ln.rule.name : null);

  return {
    id: ln.payout_line_id || ln.line_id || ln.id || idx,
    lineId: ln.payout_line_id || ln.line_id || ln.id,
    status: (ln.payout_status || ln.status || "CALCULATED").toUpperCase(),
    date: ln.date_label || formatLineDate(ln.date || ln.service_date || ln.charge_date || ln.posted_at),
    encounter: ln.encounter_type || ln.encounter || "—",
    encounterId: ln.encounter_number || ln.encounter_id || ln.admission_id || "—",
    service: ln.service_name || ln.service || ln.description || ln.charge_name || "—",
    patient: ln.patient_name || ln.patient || "—",
    code: ln.service_code || ln.code || "—",
    rule: ruleName ? { name: ruleName, code: ln.rule_code } : null,
    explain: ruleExplain,
    payoutRole: ln.payout_role || "",
    gross: num(ln.gross ?? ln.gross_amount ?? ln.gross_payout_amount ?? ln.basis_amount),
    payable: num(ln.payable ?? ln.payable_amount ?? ln.gross_payout_amount ?? ln.basis_amount),
    tds: num(ln.tds ?? ln.tds_amount),
    net: num(ln.net ?? ln.net_amount ?? ln.net_payout_amount),
    // What the patient was discounted on the charge, and what the charge was worth before
    // it. Payable is already net of the discount, so showing it is the difference between
    // a figure a doctor can check and one they have to take on trust.
    chargeAmount: num(ln.charge_gross_amount ?? ln.charge_amount),
    // Set only when somebody decided this line pays something other than the rule says.
    // `net` above is already the decided figure; these explain why it differs.
    ruleNet: ln.calculated_net_amount == null ? null : num(ln.calculated_net_amount),
    overrideReason: ln.override_reason || "",
    overriddenBy: ln.overridden_by || "",
    overriddenAt: ln.overridden_at || null,
    discount: num(ln.charge_discount_amount ?? ln.discount_amount ?? ln.discount),
  };
}

function mapProviderDetail(data) {
  const root = unwrapObject(data);
  const doctor = root.doctor || root.provider || root;
  const payCycle = root.pay_cycle || {};
  const summarySrc = root.summary || root.totals || {};
  const advancesBlock = root.advances_deposits || {};

  const lines = extractProviderLines(root).map(mapProviderLine);

  const advances = (advancesBlock.items || root.advances || doctor.advances || []).map((a, idx) => ({
    id: a.id || idx,
    date: formatLineDate(a.date || a.payment_date),
    mode: a.mode || a.payment_mode || "—",
    ref: a.reference || a.ref || a.transaction_ref || "—",
    note: a.note || a.remarks || "",
    amount: num(a.amount),
  }));

  const advancesTotal = num(
    advancesBlock.total ?? summarySrc.advances ?? summarySrc.advances_paid ?? root.advances_total,
  );
  const net = num(summarySrc.net_after_tds ?? summarySrc.net ?? root.net);
  const finalPayout = num(
    summarySrc.final ?? summarySrc.final_payout ?? root.final ?? Math.max(0, net - advancesTotal),
  );

  return {
    payCycle: {
      fromDate: payCycle.from_date || payCycle.fromDate,
      toDate: payCycle.to_date || payCycle.toDate,
      label: payCycle.label,
    },
    doc: {
      id: doctor.provider_id || root.provider_id || doctor.id,
      name: doctor.doctor_name || doctor.provider_name || doctor.name || "—",
      specialty: doctor.specialty || doctor.specialization || "—",
      empType: doctor.employment_type || doctor.emp_type || "—",
      panMasked: doctor.provider_code_masked || doctor.pan_masked || doctor.provider_code || "—",
      titleLabel: doctor.title_label || "",
      advances,
    },
    mix: root.mix || [],
    linesCount: num(root.lines_count) || lines.length,
    gross: num(summarySrc.gross ?? summarySrc.gross_billed ?? root.gross),
    payable: num(summarySrc.payable ?? summarySrc.payable_pre_tds ?? root.payable),
    tds: num(summarySrc.tds ?? summarySrc.tds_amount ?? root.tds),
    net,
    advanceTotal: advancesTotal || advances.reduce((s, a) => s + a.amount, 0),
    finalPayout,
    recovery: num(summarySrc.recovery ?? root.recovery) || Math.max(0, advancesTotal - net),
    footerNote: root.footer_note || "",
    lines,
  };
}

function formatLineDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

export function buildWizardPayload(draft, periodFrom, periodTo, providerIds) {
  return {
    period_from: periodFrom,
    period_to: periodTo,
    provider_ids: providerIds,
    payment_mode: draft.mode,
    scheduled_date: draft.scheduleDate,
    narration_prefix: draft.reference || "PAYRUN",
    approver: draft.approver,
    remarks: draft.notes || "",
    notify_doctors: Boolean(draft.notifyDoctors),
    post_to_ledger: Boolean(draft.postToLedger),
    auto_approve: true,
    auto_disburse: Boolean(draft.autoDisburse),
    provider_disbursements: [],
  };
}

function moneyStr(value) {
  return num(value).toFixed(2);
}

function normalizePaymentMode(value) {
  if (value == null || value === "") return "";
  return String(value).trim().toUpperCase().replace(/[\s-]+/g, "_");
}

function resolveDisbursedBy(...candidates) {
  for (const candidate of candidates) {
    if (candidate != null && String(candidate).trim()) {
      return String(candidate).trim();
    }
  }
  const sessionName = [
    sessionStorage.getItem("userName"),
    sessionStorage.getItem("username"),
    [sessionStorage.getItem("first_name"), sessionStorage.getItem("last_name")]
      .filter(Boolean)
      .join(" "),
  ].find((v) => v && String(v).trim());
  return sessionName ? String(sessionName).trim() : "";
}

function pickTransactionRef(run, runId) {
  if (run.transaction_ref) return String(run.transaction_ref).trim();
  if (run.disbursement_ref) return String(run.disbursement_ref).trim();
  const prefix = run.narration_prefix || run.reference_prefix;
  const runNo = run.run_number || run.pr_number || run.reference;
  if (prefix && runNo) return `${prefix}-${runNo}`;
  if (runNo) return String(runNo).trim();
  return runId ? String(runId) : "";
}

function mapProviderDisbursementRow(row, defaultRef, defaultRemarks) {
  const providerId = row.provider_id || row.providerId || row.doc?.id;
  if (!providerId) return null;
  const rowRef = row.transaction_ref || row.transaction_reference || defaultRef;
  const rowRemarks = row.remarks || row.disbursement_remarks || defaultRemarks;
  return {
    provider_id: String(providerId),
    transaction_ref: rowRef ? String(rowRef).trim() : "",
    advance_adjustment_amount: moneyStr(
      row.advance_adjustment_amount ?? row.advances ?? row.advanceTotal ?? 0,
    ),
    ...(rowRemarks ? { remarks: String(rowRemarks) } : {}),
  };
}

function extractProviderRows(root, run) {
  return (
    root.providers ||
    run.providers ||
    root.lines ||
    run.lines ||
    root.register ||
    []
  );
}

/** Build POST .../runs/{id}/disburse/ body from run detail API (+ optional UI overrides). */
export function buildDisbursePayloadFromRun(rawDetail, overrides = {}) {
  const root = unwrapObject(rawDetail);
  const run = root.run || root.payout_run || root;
  const runId = overrides.runId || run.payout_run_id || run.id || run.run_id;

  const payment_mode = normalizePaymentMode(
    overrides.payment_mode || overrides.paymentMode || run.payment_mode || run.disbursement_payment_mode,
  );
  if (!payment_mode) {
    throw new Error("Payment mode is missing on this payout run.");
  }

  const transaction_ref = String(
    overrides.transaction_ref ||
      overrides.transactionRef ||
      pickTransactionRef(
        {
          ...run,
          narration_prefix:
            overrides.narration_prefix ||
            overrides.narrationPrefix ||
            run.narration_prefix,
        },
        runId,
      ),
  ).trim();
  if (!transaction_ref) {
    throw new Error("Transaction reference is missing on this payout run.");
  }

  const defaultRemarks =
    overrides.remarks || run.remarks || run.disbursement_remarks || "";
  const disbursed_by = resolveDisbursedBy(
    overrides.disbursed_by || overrides.disbursedBy,
    run.disbursed_by,
    run.approver,
  );
  if (!disbursed_by) {
    throw new Error("Disbursed-by user could not be resolved for this payout run.");
  }

  const apiList =
    overrides.provider_disbursements ||
    run.provider_disbursements ||
    root.provider_disbursements ||
    run.disbursements;

  let provider_disbursements = [];
  if (Array.isArray(apiList) && apiList.length > 0) {
    provider_disbursements = apiList
      .map((row) => mapProviderDisbursementRow(row, transaction_ref, defaultRemarks))
      .filter(Boolean);
  } else {
    const providerRows = overrides.providers || extractProviderRows(root, run);
    provider_disbursements = (Array.isArray(providerRows) ? providerRows : [])
      .filter(
        (p) =>
          num(p.final ?? p.final_payout ?? p.finalPayout) > 0 ||
          num(p.advances ?? p.advanceTotal ?? p.advance_adjustment_amount) > 0,
      )
      .map((row) => mapProviderDisbursementRow(row, transaction_ref, defaultRemarks))
      .filter(Boolean);
  }

  if (!provider_disbursements.length) {
    throw new Error("No provider disbursements found on this payout run.");
  }

  return {
    payment_mode,
    transaction_ref,
    disbursed_by,
    provider_disbursements,
  };
}

export const RUN_STATUS_TONE = {
  DRAFT: "muted",
  CALCULATED: "info",
  GENERATED: "info",
  APPROVED: "warning",
  PAID: "success",
  DISBURSED: "success",
  CANCELLED: "danger",
  FAILED: "danger",
};

function mapPayoutRun(run) {
  return {
    id: run.payout_run_id || run.id || run.run_id,
    runNumber: run.run_number || run.pr_number || run.reference || run.payout_run_id || "—",
    status: (run.status || run.run_status || "—").toUpperCase(),
    period: run.period_label
      || formatPayCycleLabel(run.period_from || run.from_date, run.period_to || run.to_date),
    periodFrom: run.period_from || run.from_date,
    periodTo: run.period_to || run.to_date,
    paymentMode: run.payment_mode || "—",
    scheduledDate: run.scheduled_date || "—",
    doctors: num(run.provider_count ?? run.doctors ?? run.total_providers),
    total: num(run.net_disbursed ?? run.final_payout ?? run.total_amount ?? run.net_amount),
    tds: num(run.tds_amount ?? run.tds),
    createdAt: run.created_at || run.generated_at,
    approver: run.approver || "—",
  };
}

function mapPayoutRunDetail(data) {
  const root = unwrapObject(data);
  const run = root.run || root.payout_run || root;
  const base = mapPayoutRun(run);

  const providers = (root.providers || run.providers || root.lines || []).map((p) => {
    const mapped = mapRegisterRow(p);
    return {
      ...mapped,
      lineId: p.payout_line_id || p.line_id || p.id,
      status: (p.status || p.payout_status || mapped.status || "").toUpperCase() || mapped.status,
    };
  });

  return {
    ...base,
    summary: mapSummaryFields(run.summary || root.summary || run),
    providers,
    providerDisbursements: run.provider_disbursements || root.provider_disbursements || [],
    narrationPrefix: run.narration_prefix || run.reference_prefix || "",
    transactionRef: run.transaction_ref || run.disbursement_ref || "",
    bankFileReady: Boolean(run.bank_file_ready ?? root.bank_file_ready ?? true),
    notifyDoctors: Boolean(run.notify_doctors),
    postToLedger: Boolean(run.post_to_ledger),
    remarks: run.remarks || "",
  };
}
